import { Button, DatePicker, Space, Typography } from "antd";
import moment from "moment";
import FilterComponent from "components/Configuration/CommonComponents/FilterComponent";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { cglFormatDate } from "utils/functions";
import { getData } from "../../../../apiServices/common";
import AppContent from "../../../../common/AppContent";
import HandleNotification from "../../../../common/Notification";
import AppPageHeader from "../../../../common/PageHeader";
import ExtraContent from "../../../Configuration/CommonComponents/ExtraContent";
import SewingToFoldingTable from "./FinishingHourlyIronOriginalTable";
import FinishingHourlyIronOriginalTable from "./FinishingHourlyIronOriginalTable";
import { constructTitle } from "../helpers";

const HourlyIronOriginal = (props) => {
  // Router
  const { type } = props;
  const location = useLocation();
  const routerPath = location ? location.pathname : null;
  const navigate = useNavigate();

  // States
  const [showFilter, setShowFilter] = useState(
    localStorage.getItem("filter_status") && routerPath
      ? JSON.parse(localStorage.getItem("filter_status"))[routerPath]
      : false,
  );
  const [page, setPage] = useState(1);
  const [refresh, setRefresh] = useState(0);
  const [tableData, setTableData] = useState(null);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [filterValues, setFilterValues] = useState({});
  console.log("🚀 ~ HourlyIron ~ filterValues:", filterValues);

  // ux2(C1): entry_date has no default filter today; open on the last 30 days so the page
  // isn't blank, while the endpoint stays backward compatible when from_date/to_date are absent.
  const getDefaultDateRange = () => [moment().subtract(29, "days"), moment()];
  const [dateRange, setDateRange] = useState(getDefaultDateRange);

  // Guard against setState after unmount and against a slower, stale request clobbering a
  // faster, later one (round-1 pattern).
  const mountedRef = useRef(true);
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);
  const reqIdRef = useRef(0);

  // Antd
  const { Title } = Typography;

  // Filter open/close status update in localStorage
  useEffect(() => {
    const filterStatus = localStorage.getItem("filter_status");
    const parsedFilterStatus = filterStatus ? JSON.parse(filterStatus) : {};

    if (showFilter) {
      parsedFilterStatus[routerPath] = true;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    } else {
      parsedFilterStatus[routerPath] = false;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    }
  }, [showFilter, routerPath, location]);

  const onFilterChange = (values) => {
    setPage(1);
    setFilterValues(values || {});
  };

  const fetchData = useCallback(
    async (filterValues) => {
      const myReq = ++reqIdRef.current;
      const query = `/api/digitalization/hourly-${type}-list`;
      const bodyData = {
        page: filterValues?.page ?? page,
        per_page: filterValues?.per_page || 10,
        ...(filterValues?.finishing_line_id && {
          finishing_line_id: filterValues?.finishing_line_id,
        }),
        ...(filterValues?.style_id && { style_id: filterValues?.style_id }),
        ...(filterValues?.sub_style_id && {
          sub_style_id: filterValues?.sub_style_id,
        }),
        ...(filterValues?.original_po && {
          original_po: filterValues?.original_po,
        }),
        ...(filterValues?.po_map_id && {
          po_map_id: filterValues?.po_map_id,
        }),
        ...(filterValues?.entry_date && {
          entry_date: filterValues?.entry_date,
        }),
        // ux2(C1): default/selected date window; an explicit entry_date filter takes precedence so the two
        // never intersect to an empty list.
        ...(!filterValues?.entry_date && dateRange?.[0] && dateRange?.[1] && {
          from_date: dateRange[0].format("YYYY-MM-DD"),
          to_date: dateRange[1].format("YYYY-MM-DD"),
        }),
      };

      const response = await getData(query, false, bodyData);

      // Stale-response guard: drop this response if the component unmounted or a newer
      // request (e.g. the user changing the date range again) has since been fired.
      if (!mountedRef.current || myReq !== reqIdRef.current) return;

      if (response && response?.data?.code === 200) {
        setTableData(response?.data?.data?.data);
        setPageSize(response?.data?.data?.per_page);
        setTotal(response?.data?.data?.total);
      } else {
        HandleNotification("error", "Error", "Something went wrong");
      }
    },
    [page, type, dateRange],
  );

  useEffect(() => {
    const value = {
      ...filterValues,
      page: page,
    };
    fetchData(value);
  }, [refresh, page, pageSize, filterValues, type, fetchData]);

  const onPageChange = (newPage) => {
    setPage(newPage);
  };

  const handleDateRangeChange = (dates) => {
    setPage(1);
    setDateRange(dates && dates[0] && dates[1] ? dates : getDefaultDateRange());
  };

  const handleEdit = (event, record) => {
    const params = Object.fromEntries(
      Object.entries({
        entry_date: record?.entry_date || "",
        finishing_line_id: record?.finishing_line_id || "",
        supervisor_id: record?.supervisor_id || "",
        style_id: record?.style_id || "",
        sales_contract_id: record?.sales_contract_id || "",
        buying_office_id: record?.buying_office_id || null,
        buyer_id: record?.buyer_id || null,
        sub_contract_factory_id: record?.sub_contract_factory_id || null,
        sub_style_id: record?.sub_style_id || null,
        po_map_id: record?.po_map_id || null,
        color_id: record?.color_id || "",
        size_id: record?.size_id || "",
      }).filter(([_, value]) => value !== null && value !== ""),
    );

    const searchParams = new URLSearchParams(params).toString();
    console.log("🚀 ~ handleEdit ~ searchParams:", searchParams);

    navigate(`/digitalization/finishing/hourly-${type}/edit/${record.id}?${searchParams}`);
  };

  const tableColumns = [
    {
      title: "Entry Date",
      dataIndex: "entry_date",
      key: "entry_date",
      width: "140px",
      render: (row) => cglFormatDate(row),
    },
    {
      title: "Hour",
      dataIndex: "hour",
      key: "hour",
      width: "40px",
    },
    {
      title: "Time Range",
      key: "time_range",
      width: "140px",
      render: (row) => `${row.hour_time_range_from} - ${row.hour_time_range_to}`,
    },
    {
      title: "Finishing Line",
      dataIndex: ["finishing_line_info", "name"],
      key: "finishing_line",
      width: "140px",
    },
    {
      title: "Supervisor",
      dataIndex: ["supervisor_info", "name"],
      key: "supervisor",
      width: "140px",
    },

    {
      title: "Style",
      dataIndex: ["style_info", "style_no"],
      key: "style",
      width: "100px",
    },
    {
      title: "Factory",
      dataIndex: ["sub_contract_factory_info", "name"],
      key: "sub_contract_factory_name",
      width: "120px",
    },
    {
      title: "Sales Contract",
      dataIndex: ["sales_contract_info", "reference_no"],
      key: "sales_contract",
      width: "150px",
    },
    {
      title: "Buying Office",
      dataIndex: ["buying_office_info", "name"],
      key: "buying_office",
      width: "150px",
    },
    {
      title: "Buyer",
      dataIndex: ["buyer_info", "name"],
      key: "buyer",
      width: "150px",
    },
    {
      title: "PO",
      dataIndex: ["po_map_info", "original_po"],
      key: "po",
      width: "120px",
    },
    {
      title: "Color",
      dataIndex: ["color_info", "name"],
      key: "color",
      width: "120px",
    },
    {
      title: "Size",
      dataIndex: ["size_info", "name"],
      key: "size",
      width: "80px",
    },
    {
      title: "Qty",
      dataIndex: "quantity",
      key: "quantity",
      width: "50px",
    },
    {
      title: "Action",
      key: "action",
      width: "20px",
      render: (_, record) => (
        <div>
          <Button
            style={{ marginLeft: 5 }}
            size="small"
            onClick={(event) => {
              handleEdit(event, record);
            }}
            // disabled={!hasPermission([USER_EDIT_PERMISSION])}
          >
            Edit
          </Button>
        </div>
      ),
    },
  ];
  const memoizedCustomFilter = useMemo(
    () => (
      <FilterComponent
        tableData={tableData}
        filterAPI={onFilterChange}
        filterName={"finishing-hourly-iron"}
        setShowFilter={setShowFilter}
        showFilter={showFilter}
      />
    ),
    [showFilter],
  );

  const header = (
    <AppPageHeader
      extra={
        <ExtraContent
          refresh={refresh}
          setRefresh={setRefresh}
          handleAddItemOpen={fetchData}
          hideAddButton={true}
          setShowFilter={setShowFilter}
          showFilter={showFilter}
        />
      }
      title={<Title level={5}>{constructTitle(type)}</Title>}
    />
  );
  const content = (
    <>
      <Space style={{ marginBottom: 10 }}>
        <span>Entry Date:</span>
        <DatePicker.RangePicker size="small" value={dateRange} onChange={handleDateRangeChange} allowClear={false} />
      </Space>
      {memoizedCustomFilter}
      <FinishingHourlyIronOriginalTable
        onChange={(newPage) => {
          onPageChange(newPage);
        }}
        total={total}
        current={page}
        defaultCurrent={page}
        pageSize={pageSize}
        dataSource={tableData}
        tableColumns={tableColumns}
        pagination={true}
        emptyHint={`No records between ${dateRange?.[0]?.format("DD-MMM-YYYY")} and ${dateRange?.[1]?.format("DD-MMM-YYYY")}. Widen the range above.`}
      />
    </>
  );
  return (
    <>
      <AppContent header={header} content={content} />
    </>
  );
};

export default HourlyIronOriginal;
